<template>
  <transition name="modal-fade">
    <div class="modal-backdrop">
      <div
        class="modal modal-xl"
        role="dialog"
        aria-labelledby="modalTitle"
        aria-describedby="modalDescription"
      >
        <header class="modal-header" id="modalTitle">
          <div class="container-fluid">
            <div class="row">
              <div class="col-10">
                <h5 class="modal-title" id="orderModalLabel">
                  {{ $t('shop.current-scent') }}
                  <strong>{{ currentScent }}</strong
                  >, {{ $t('shop.choose-next') }}
                </h5>
              </div>
              <div class="col-2">
                <button
                  type="button"
                  class="close"
                  @click="closeShop"
                  aria-label="Close modal"
                >
                  x
                </button>
              </div>
            </div>
          </div>
        </header>
        <section class="modal-body" id="modalDescription">
          <div class="container-fluid">
            <div class="row mb-3">
              <div class="col-lg-8">
                <p>{{ $t('shop.choose-scent') }}</p>
              </div>
              <div class="col-lg-4">
                <div class="form-group d-flex align-items-center">
                  <label class="label-search">{{ $t('home.search') }}</label>
                  <input
                    v-model="search"
                    type="search"
                    class="form-control"
                    id="searchScent"
                    :placeholder="$t('shop.search-placeholder')"
                  />
                </div>
              </div>
              <div class="col-lg-12">
                <Message
                  v-if="submittable"
                  :message="$t('shop.max-scents')"
                  type="info"
                />
              </div>
            </div>
            <div class="row mb-1">
              <Spinner v-if="!displayedScents" />
              <Product
                v-else
                v-for="scent in displayedScents"
                :key="scent.id"
                :scentId="scent.id"
                :cartIds="cart"
                :allowed="maxCartAmount"
                @add-to-cart="addToCart"
                @remove-from-cart="removeFromCart"
              />
            </div>
            <div class="row mb-3">
              <div class="col d-flex justify-content-center">
                <nav aria-label="Page navigation">
                  <ul class="pagination mb-0">
                    <li class="page-item" :class="{ disabled: page === 1 }">
                      <a
                        type="button"
                        class="page-link"
                        @click="page--"
                        href="#"
                        aria-label="Previous"
                      >
                        <span aria-hidden="true">&laquo;</span>
                      </a>
                    </li>
                    <li
                      v-for="pageNumber in pages"
                      class="page-item d-none d-lg-block"
                      :class="{ active: page === pageNumber }"
                      v-bind:key="pageNumber.index"
                    >
                      <a
                        type="button"
                        class="page-link"
                        @click="page = pageNumber"
                        href="#"
                        >{{ pageNumber }}</a
                      >
                    </li>
                    <li
                      class="page-item"
                      :class="{ disabled: page === pages.length }"
                    >
                      <a
                        type="button"
                        @click="page++"
                        class="page-link"
                        href="#"
                        aria-label="Next"
                      >
                        <span aria-hidden="true">&raquo;</span>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-footer">
          <div class="container-fluid">
            <div class="row">
              <div v-if="cart.length" class="col-lg-8 mb-3">
                <h2 class="product-choice">{{ $t('shop.your-choice') }}</h2>
                <div
                  v-for="product in products"
                  :key="product.key"
                  class="product d-flex align-items-center mb-1"
                >
                  <img :src="product.image.format.small.url" />
                  <div class="ml-3">
                    <span>{{ amount }}x</span> {{ product.title }}
                  </div>
                  <a @click="removeAllFromCart(product.id)"
                    ><MaterialSvg
                      class="icon-red ml-3"
                      name="delete"
                      :width="22"
                  /></a>
                </div>
                <span v-if="serviceVisit" class="small">{{
                  $t('shop.service-visit')
                }}</span>
              </div>
              <div v-else class="col-lg-12">{{ $t('shop.empty') }}</div>
              <div class="col-lg-12 d-flex flex-row-reverse">
                <button
                  @click="placeOrder"
                  type="button"
                  :class="{ 'form-cannot-submit': !submittable }"
                  class="btn btn-primary"
                >
                  {{ $t('shop.place-request') }}
                </button>
                <button
                  @click="scentHelp"
                  type="button"
                  class="btn btn-link mr-3"
                >
                  {{ $t('shop.help') }}
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import Product from '@/components/Product.vue';
import { Action as ShopAction } from '@/store/shop/types';
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'shop',
  components: {
    Product,
  },
  data() {
    return {
      page: 1,
      perPage: 12,
      pages: [],
      cart: [],
      maxCartAmount: 2,
      search: '',
    };
  },
  props: {
    hubId: {
      type: Number,
      required: true,
    },
    currentScent: {
      type: String,
      required: false,
    },
  },
  computed: {
    ...mapState({
      scents: (state) => state.scents.list,
    }),
    submittable() {
      return this.cart.length === this.maxCartAmount;
    },
    displayedScents() {
      return this.paginate(this.filteredList);
    },
    filteredList() {
      return this.scents.filter((scent) => {
        return scent.title.toLowerCase().includes(this.search.toLowerCase());
      });
    },
    serviceVisit() {
      return this.$store.state.user.subscription.visit;
    },
    products() {
      return this.getScentsById(this.cart);
    },
    amount() {
      let amount = 1;
      if (this.products.length === 1 && this.submittable) {
        amount = 2;
      }
      return amount;
    },
    ...mapGetters({
      getScentsById: ['scents/getScentsById'],
      getScentNameById: ['scents/getScentNameById'],
    }),
  },
  created() {
    this.setPages();
  },
  watch: {
    filteredList() {
      this.setPages();
    },
  },
  methods: {
    scentHelp() {
      this.$router.push({
        name: 'Contact',
        params: { question: 'scent-choice' },
      });
      this.$root.$emit('close');
    },
    getTitlesById(id) {
      return this.getScentsById(id);
    },
    placeOrder() {
      if (this.submittable) {
        let ids = this.cart;
        let titles = [];
        ids.forEach((id) => {
          titles.push(this.getScentNameById(id));
        });
        this.$store
          .dispatch(`${ShopAction.SEND_ORDER}`, {
            hubId: this.hubId,
            scents: titles,
          })
          .then((resp) => {
            this.$router.push({
              name: 'OrderComplete',
              params: { ids: ids.join(',') },
            });
            this.$root.$emit('close');
          })
          .catch((err) => {
            console.error('PlaceOrder: ' + err);
            this.$router.push({ name: 'OrderFailed' });
            this.$root.$emit('close');
          });
      }
    },
    addToCart(scentId) {
      this.cart.push(scentId);
    },
    removeFromCart(scentId) {
      const index = this.cart.indexOf(scentId);
      if (index >= 0) {
        this.cart.splice(index, 1);
      }
    },
    removeAllFromCart(productId) {
      console.log(productId);
      this.cart = this.cart.filter((id) => id !== productId);
    },
    close() {
      this.$emit('close');
    },
    closeShop() {
      this.$root.$emit('close');
    },
    setPages() {
      // Reset pager.
      this.page = 1;
      this.pages = [];
      let numberOfPages = Math.ceil(this.filteredList.length / this.perPage);
      for (let index = 1; index <= numberOfPages; index++) {
        this.pages.push(index);
      }
    },
    paginate(items) {
      let page = this.page;
      let perPage = this.perPage;
      let from = page * perPage - perPage;
      let to = page * perPage;
      return items.slice(from, to);
    },
  },
};
</script>

<style scoped lang="scss">
.product {
  img {
    height: 75px;
    width: 75px;
    border-radius: $border-radius;
    object-fit: cover;
  }
}
</style>
